import type { Entry, Merchant } from '@taler/api-client'
import type { LocalDate } from '@taler/domain'
import type { Frecency } from './targets.ts'

export type { Merchant }

export interface RankedMerchant {
  readonly merchant: Merchant
  readonly frecency: Frecency | undefined
}

/** Same shape as {@link computeFrecency} in `targets.ts`, keyed by `entry.merchantId`
 * (entry-level, not posting-level - a merchant is the payee of a whole entry). */
export function computeMerchantFrecency(
  entries: readonly Entry[],
  today: LocalDate,
): ReadonlyMap<string, Frecency> {
  const out = new Map<string, Frecency>()
  for (const entry of entries) {
    if (entry.merchantId === undefined) continue
    const daysAgo = today.epochDay - entry.date.epochDay
    const existing = out.get(entry.merchantId)
    out.set(entry.merchantId, {
      count: (existing?.count ?? 0) + 1,
      lastUsedDaysAgo: existing === undefined ? daysAgo : Math.min(existing.lastUsedDaysAgo, daysAgo),
    })
  }
  return out
}

/** Most-used-first, then alphabetical - same ordering `AccountCombobox` renders, applied
 * to merchants instead of accounts/contacts. */
export function rankMerchants(
  merchants: readonly Merchant[],
  frecency: ReadonlyMap<string, Frecency>,
): RankedMerchant[] {
  return [...merchants]
    .map((merchant) => ({ merchant, frecency: frecency.get(merchant.id) }))
    .sort((a, b) => {
      const byCount = (b.frecency?.count ?? 0) - (a.frecency?.count ?? 0)
      if (byCount !== 0) return byCount
      const byRecency = (a.frecency?.lastUsedDaysAgo ?? Infinity) - (b.frecency?.lastUsedDaysAgo ?? Infinity)
      if (byRecency !== 0) return byRecency
      return a.merchant.name.localeCompare(b.merchant.name)
    })
}

export function matchesMerchantQuery(merchant: Merchant, query: string): boolean {
  if (query.trim() === '') return true
  return merchant.name.toLowerCase().includes(query.trim().toLowerCase())
}
